// components/Navbar.tsx
'use client';

import { useEffect, useState, useRef, Suspense } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useSearchParams } from 'next/navigation';
import { getLangFromPath } from '@/lib/request';
import { useSiteData } from '@/contexts/SiteDataContext';
import { locales } from '@/lib/config';
import { processImageUrl } from '../lib/imageUrl';

interface NavbarProps {
  isVisible: boolean;
}

interface NavItem {
  name: string;
  url: string;
  badge?: number;
  children?: NavItem[];
}

interface Language {
  code: string;
  name: string;
  flag: string;
  localName: string;
}

const languages: Language[] = [
  { code: 'en-us', name: 'English', flag: '🇬🇧', localName: 'English' },
  { code: 'ar-sa', name: 'اللغة العربية', flag: '🇸🇦', localName: 'اللغة العربية' },
  // { code: 'ja-jp', name: '日本語', flag: '🇯🇵', localName: '日本語' },
  // { code: 'ko-kr', name: '한국어', flag: '🇰🇷', localName: '한국어' },
  // { code: 'zh-cn', name: '中文', flag: '🇨🇳', localName: '中文' },
   { code: 'fr-fr', name: 'French', flag: '🇫🇷', localName: 'French' },
   { code: 'de-de', name: 'German', flag: '🇩🇪', localName: 'German' },
   { code: 'es-es', name: 'Spanish', flag: '🇪🇸', localName: 'Spanish' },
   { code: 'pt-pt', name: 'Português', flag: '🇵🇹', localName: 'Português' },
];

const PRODUCT_URL_REWRITES: Record<string, string> = {
  '/products/yyrh-mr-1000-hd': '/products/stormwater-attenuation-crate/yyrh-mr1000-hurricane-hd',
  '/products/yyrh-mr-1000-ew': '/products/heavy-duty-modular-stormwater-tanks/yyrh-mr1000-ew',
  '/products/yyrh-mr-1200-ew': '/products/stormwater-detention-modules/yyrh-mr1200-ew',
  '/products/yyrh-mr-1200-sd': '/products/heavy-modular-underground-stormwater-tank/yyrh-mr1200-sd',
};

function rewriteLegacyProductUrl(url: string): string {
  if (!url || url.startsWith('http')) return url;
  const localePattern = locales.join('|');
  const match = url.match(new RegExp(`^(/(?:${localePattern}))?(/products/[^/]+)$`));
  if (match) {
    const legacyPath = match[2];
    const rewrite = PRODUCT_URL_REWRITES[legacyPath];
    if (rewrite) {
      const localePrefix = match[1] || '';
      return `${localePrefix}${rewrite}`;
    }
  }
  return url;
}

const defaultNavLinks: NavItem[] = [
  { name: 'Home', url: '/' },
  {
    name: 'Products',
    url: '/products',
    children: [
      {
        name: 'Application',
        url: '/products',
        children: [
          { name: 'Product A', url: '/products/a' },
          { name: 'Product B', url: '/products/b' },
          { name: 'Product C', url: '/products/c' },
        ],
      },
      {
        name: 'Industry',
        url: '/products',
        children: [
          { name: 'Industrial', url: '/products/industrial' },
          { name: 'Commercial', url: '/products/commercial' },
        ],
      },
    ],
  },
];

const ExpandArrow = ({ isExpanded }: { isExpanded: boolean }) => (
  <svg
    className={`expand-arrow ${isExpanded ? 'expanded' : ''}`}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
  >
    <path
      d="M4.5 2.5L8 6L4.5 9.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const isPathInSubtree = (item: NavItem, path: string): boolean => {
  if (item.url === path) return true;
  if (item.url === '/' && (path === '/' || /^\/[a-z]{2}(-[a-z]{2})?$/i.test(path))) return true;
  if (item.children) {
    return item.children.some((child) => isPathInSubtree(child, path));
  }
  return false;
};

interface NestedMenuItemProps {
  item: NavItem;
  level: number;
  onNavClick: (e: React.MouseEvent, link: NavItem) => void;
  currentPath: string;
  defaultExpanded?: boolean;
  getNavLinkHref: (url: string) => string;
}

const NestedMenuItem = ({
  item,
  level,
  onNavClick,
  currentPath,
  defaultExpanded = false,
  getNavLinkHref,
}: NestedMenuItemProps) => {
  const isExternal = item.url.startsWith('http');
  const isActive = currentPath === item.url || (item.url === '/' && (currentPath === '/' || /^\/[a-z]{2}(-[a-z]{2})?$/i.test(currentPath)));
  const hasChildren = !!(item.children && item.children.length > 0);
  const [isExpanded, setIsExpanded] = useState(
    defaultExpanded || isPathInSubtree(item, currentPath)
  );
  const paddingLeft = level * 16 + 12;

  const toggleExpand = (e: React.MouseEvent) => {
    if (hasChildren) {
      e.preventDefault();
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div className="nested-menu-item">
      <div className="nested-menu-row">
        {isExternal ? (
          <a
            href={item.url}
            className={`nested-menu-link level-${level}  textbg`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ paddingLeft: `${paddingLeft}px` }}
          >
            <span className="menu-text">{item.name}</span>
            {item.badge ? <span className="badge">{item.badge}</span> : null}
          </a>
        ) : (
          <div
            className="menu-item-wrapper"
            style={{ paddingLeft: `${paddingLeft}px` }}
            onClick={toggleExpand}
          >
            <Link
              href={getNavLinkHref(item.url)}
              className={`nested-menu-link level-${level} ${isActive ? 'active' : ''}`}
              onClick={(e) => {
                if (!hasChildren) {
                  onNavClick(e, item);
                }
              }}
            >
              <span className="menu-text">{item.name}</span>
              {item.badge ? <span className="badge">{item.badge}</span> : null}
            </Link>

            {hasChildren && (
              <button
                className={`expand-btn ${isExpanded ? 'expanded' : ''}`}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                <ExpandArrow isExpanded={isExpanded} />
              </button>
            )}
          </div>
        )}
      </div>

      {hasChildren && (
        <div className={`nested-children ${isExpanded ? 'expanded' : 'collapsed'}`}>
          {item.children!.map((child, index) => (
            <NestedMenuItem
              key={`${child.url}-${index}-${level + 1}`}
              item={child}
              level={level + 1}
              onNavClick={onNavClick}
              currentPath={currentPath}
              defaultExpanded={false}
              getNavLinkHref={getNavLinkHref}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface DropdownMenuProps {
  items: NavItem[];
  isVisible: boolean;
  title: string;
  onNavClick: (e: React.MouseEvent, link: NavItem) => void;
  currentPath: string;
  getNavLinkHref: (url: string) => string;
}

const DropdownMenu = ({
  items,
  isVisible,
  onNavClick,
  currentPath,
  getNavLinkHref,
}: DropdownMenuProps) => {
  if (!isVisible) return null;

  return (
    <div className={`dropdown-menu-vertical ${isVisible ? 'show' : ''}`}>
      <div className="dropdown-menu-content">
        {items.map((item, index) => (
          <NestedMenuItem
            key={`${item.url}-${index}-1`}
            item={item}
            level={1}
            onNavClick={onNavClick}
            currentPath={currentPath}
            defaultExpanded={true}
            getNavLinkHref={getNavLinkHref}
          />
        ))}
      </div>
    </div>
  );
};

// 内部组件，包含实际的导航逻辑
function NavbarInternal({ isVisible }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const [showLogo1, setShowLogo1] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [searchString, setSearchString] = useState('');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownTimeoutRef = useRef<number | null>(null);
  // 从 pathname 直接提取语言，确保服务端和客户端一致
  const currentLang = (() => {
    const pathSegments = pathname.split('/').filter(Boolean);
    const urlLang = pathSegments[0];
    if (urlLang && /^[a-z]{2}(-[a-z]{2})?$/i.test(urlLang)) {
      return urlLang;
    }
    return 'en-us';
  })();
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [showStaticBg, setShowStaticBg] = useState<Record<string, boolean>>({});
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMobileItems, setExpandedMobileItems] = useState<Record<string, boolean>>({});

  const { settingData, menuData } = useSiteData();

  // currentLang 直接从 pathname 计算，不需要 useEffect 更新

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setIsLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 100;
      setIsScrolled(scrolled);
      setShowLogo1(scrolled);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseEnter = (name: string) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setActiveDropdown(name);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = window.setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  const handleLinkMouseEnter = (linkName: string) => {
    setHoveredLink(linkName);
    setShowStaticBg((prev) => ({ ...prev, [linkName]: false }));
    setTimeout(() => {
      setShowStaticBg((prev) => ({ ...prev, [linkName]: true }));
    }, 1000);
  };

  const handleLinkMouseLeave = (linkName: string) => {
    setHoveredLink(null);
    setShowStaticBg((prev) => ({ ...prev, [linkName]: false }));
  };

  const handleLanguageChange = (langCode: string) => {
    console.log('【语言切换】开始, langCode:', langCode);
    setIsLangDropdownOpen(false);
    const currentPath = pathname;
    const currentSearch = window.location.search;
    console.log('【语言切换】currentPath:', currentPath, 'currentSearch:', currentSearch);
    const pathSegments = currentPath.split('/').filter(Boolean);
    const currentLangPrefix = pathSegments[0];
    const isLangPrefix = /^[a-z]{2}(-[a-z]{2})?$/i.test(currentLangPrefix);
    let newPath: string;
    if (isLangPrefix) {
      if (pathSegments.length === 1) newPath = `/${langCode}`;
      else {
        pathSegments[0] = langCode;
        newPath = '/' + pathSegments.join('/');
      }
    } else {
      newPath = `/${langCode}${currentPath === '/' ? '' : currentPath}`;
    }
    const finalUrl = `${newPath}${currentSearch}${window.location.hash}`;
    console.log('【语言切换】最终 URL:', finalUrl);
    window.location.replace(finalUrl);
  };

  const handleNavClick = (e: React.MouseEvent, link: NavItem) => {
    if (link.url.startsWith('http')) return;
    
    // 获取目标 URL（带语言前缀）
    const targetUrl = getNavLinkHref(link.url);
    
    // 检查当前 URL 是否包含查询参数
    const hasQueryParams = window.location.search !== '';
    
    // 如果目标页面与当前页面相同（考虑语言前缀）且有查询参数
    const isSamePage = pathname === targetUrl || 
                       (link.url === '/news' && pathname === `/${currentLang}/news`);
    
    if (isSamePage && hasQueryParams) {
      // 强制刷新页面以清除查询参数
      e.preventDefault();
      window.location.href = targetUrl;
      return;
    }
    
    if (link.url === pathname) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (link.url.includes('#')) {
      e.preventDefault();
      const h = link.url.split('#')[1];
      const el = document.getElementById(h);
      el?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileItem = (itemName: string) => {
    setExpandedMobileItems((prev) => ({
      ...prev,
      [itemName]: !prev[itemName],
    }));
  };

  const getNavLinkHref = (url: string) => {
    const rewritten = rewriteLegacyProductUrl(url);
    if (rewritten.startsWith('http')) return rewritten;
    const hasLocalePrefix = locales.some(locale => rewritten.startsWith(`/${locale}`));
    if (hasLocalePrefix) {
      return rewritten;
    }
    if (rewritten === '/') return '/';
    return `/${currentLang}${rewritten}`;
  };

  const hasChildren = (item: NavItem): boolean => !!(item.children && item.children.length > 0);
  // 确保 menuData 是数组类型
  const isValidMenuData = Array.isArray(menuData);
  const displayNavItems = isValidMenuData && menuData.length > 0 ? menuData : defaultNavLinks;
  const currentLanguage = languages.find((l) => l.code === currentLang) || languages[0];

  if (!isVisible) return null;

  const renderMobileMenuItem = (item: NavItem, level: number = 0, parentKey: string = '') => {
    const isExternal = item.url.startsWith('http');
    const itemKey = parentKey ? `${parentKey}-${item.name}` : item.name;
    const isExpanded = expandedMobileItems[itemKey];
    const linkHasChildren = hasChildren(item);
    const isActive =
      pathname === item.url ||
      (item.url === '/' && (pathname === '/' || /^\/[a-z]{2}(-[a-z]{2})?$/i.test(pathname))) ||
      (linkHasChildren &&
        item.children?.some(
          (child: NavItem) => pathname === child.url || isPathInSubtree(child, pathname)
        ));
    const paddingLeft = level * 16 + 20;

    if (isExternal) {
      return (
        <div key={itemKey} className="mobile-menu-item" style={{ paddingLeft: `${paddingLeft}px` }}>
          <a
            href={item.url}
            className="mobile-nav-link external"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {item.name}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 17L17 7M17 7H7M17 7V17" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      );
    }

    return (
      <div key={itemKey} className="mobile-menu-item">
          <div className="mobile-nav-link-wrapper" style={{ paddingLeft: `${paddingLeft}px` }}>
          <Link
            href={getNavLinkHref(item.url)}
            className={`mobile-nav-link ${isActive ? 'active' : ''}`}
            onClick={(e) => {
              handleNavClick(e, item);
            }}
          >
            {item.name}
          </Link>
          {linkHasChildren && (
            <button
              className={`mobile-expand-btn ${isExpanded ? 'expanded' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleMobileItem(itemKey);
              }}
              aria-label={isExpanded ? 'Collapse submenu' : 'Expand submenu'}
              aria-expanded={isExpanded}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>

        {linkHasChildren && (
          <div className={`mobile-submenu ${isExpanded ? 'expanded' : ''}`}>
            {item.children!.map((child, index) => renderMobileMenuItem(child, level + 1, itemKey))}
          </div>
        )}
      </div>
    );
  };

  return (
    <nav ref={navRef} className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        <Link href="/" className="navbar-logo">
          {(() => {
            const initialSrc = settingData?.baseJson?.['item_1776682931661_8ft28ep8p']?.value || '';
            const scrolledSrc = settingData?.baseJson?.['item_1776682938862_53adycy9h']?.value || '';

            if (showLogo1) {
              return scrolledSrc ? (
                <Image
                  src={scrolledSrc}
                  alt="logo"
                  fill
                  sizes="(max-width: 768px) 120px, (max-width: 1200px) 130px, 160px"
                  className="object-contain"
                />
              ) : null;
            }
            return initialSrc ? (
              <Image
                src={initialSrc}
                alt="logo"
                fill
                priority
                sizes="(max-width: 768px) 120px, (max-width: 1200px) 130px, 160px"
                className="object-contain"
              />
            ) : null;
          })()}
        </Link>

        <ul className="navbar-links desktop-only">
          {displayNavItems.map((link, index) => {
            const isExternal = link.url.startsWith('http');
            const linkHasChildren = hasChildren(link);
            const isDropdownActive = activeDropdown === link.name;
            const uniqueKey = `${link.url}-${index}`;
            const isHovered = hoveredLink === link.name;
            const showStatic = showStaticBg[link.name];

            if (isExternal) {
              return (
                <li key={uniqueKey}>
                  <a href={link.url} className="navbar-link" target="_blank" rel="noopener noreferrer">
                    {link.name}
                  </a>
                </li>
              );
            }

            const isActive =
              // 处理路径匹配，忽略尾随斜杠
              pathname === link.url || pathname === link.url.replace(/\/$/, '') ||
              // 处理 Home 链接的特殊情况
              (link.name === 'Home' && (pathname === '/' || /^\/[a-z]{2}(-[a-z]{2})?$/i.test(pathname))) ||
              // 处理深层嵌套路由的包含匹配
              (linkHasChildren && isPathInSubtree(link, pathname));
            
            // 添加调试日志
           
          

            if (linkHasChildren) {
              return (
                <li
                  key={uniqueKey}
                  className="navbar-item-has-children"
                  onMouseEnter={() => handleMouseEnter(link.name)}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link
                    href={getNavLinkHref(link.url)}
                    className={`navbar-link ${isActive ? 'active' : ''} ${isDropdownActive ? 'dropdown-active' : ''}`}
                    onClick={(e) => handleNavClick(e, link)}
                    onMouseEnter={() => handleLinkMouseEnter(link.name)}
                    onMouseLeave={() => handleLinkMouseLeave(link.name)}
                  >
                    <span className="link-bg link-bg-gif" style={{ opacity: isHovered && !showStatic ? 1 : 0 }} />
                    <span className="link-bg link-bg-png" style={{ opacity: isHovered && showStatic ? 1 : 0 }} />
                    <span className="link-text">
                      {link.name}
                      <svg
                        className={`dropdown-arrow ${isDropdownActive ? 'rotate' : ''}`}
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2.5 4.5L6 8L9.5 4.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {link.badge && <span className="badge">{link.badge}</span>}
                    </span>
                  </Link>

                  <DropdownMenu
                    items={link.children!}
                    isVisible={isDropdownActive}
                    title={link.name}
                    onNavClick={handleNavClick}
                    currentPath={pathname}
                    getNavLinkHref={getNavLinkHref}
                  />
                </li>
              );
            }

            return (
              <li key={uniqueKey}>
                <Link
                  href={getNavLinkHref(link.url)}
                  className={`navbar-link ${isActive ? 'active' : ''}`}
                  onClick={(e) => handleNavClick(e, link)}
                  onMouseEnter={() => handleLinkMouseEnter(link.name)}
                  onMouseLeave={() => handleLinkMouseLeave(link.name)}
                >
                  <span className="link-bg link-bg-gif" style={{ opacity: isHovered && !showStatic ? 1 : 0 }} />
                  <span className="link-bg link-bg-png" style={{ opacity: isHovered && showStatic ? 1 : 0 }} />
                  <span className="link-text">
                    {link.name}
                    {link.badge && <span className="badge">{link.badge}</span>}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>

       

        <div className="desktop-only language-switcher" ref={langDropdownRef}>
          <button
            className="language-trigger"
            onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
          >
           
            <span className="language-code">{currentLanguage.name}</span>
            <svg
              className={`language-arrow ${isLangDropdownOpen ? 'rotate' : ''}`}
              width="10"
              height="10"
              viewBox="0 0 12 12"
              fill="none"
            >
              <path
                d="M2.5 4.5L6 8L9.5 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div className={`language-dropdown ${isLangDropdownOpen ? 'show' : ''}`}>
            <div className="language-dropdown-header">
              <span>Select Language</span>
            </div>
            <ul className="language-list">
              {languages.map((lang) => {
                // 构建语言切换的 URL
                const pathSegments = pathname.split('/').filter(Boolean);
                const currentLangPrefix = pathSegments[0];
                const isLangPrefix = /^[a-z]{2}(-[a-z]{2})?$/i.test(currentLangPrefix);
                let newPath: string;
                if (isLangPrefix) {
                  if (pathSegments.length === 1) newPath = `/${lang.code}`;
                  else {
                    pathSegments[0] = lang.code;
                    newPath = '/' + pathSegments.join('/');
                  }
                } else {
                  newPath = `/${lang.code}${pathname === '/' ? '' : pathname}`;
                }
                const langUrl = `${newPath}${searchString}`;
                
                return (
                  <li key={lang.code}>
                    <a
                      href={langUrl}
                      className={`language-option ${currentLang === lang.code ? 'active' : ''}`}
                      onClick={(e) => setIsLangDropdownOpen(false)}
                    >
                      <span className="language-option-flag">{lang.flag}</span>
                      <div className="language-option-info">
                        <span className="language-option-name">{lang.name}</span>
                        <span className="language-option-local">{lang.localName}</span>
                      </div>
                    {currentLang === lang.code && (
                      <svg
                        className="check-icon"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    )}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <button
          className={`mobile-menu-btn ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
      </div>

      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'show' : ''}`} onClick={() => setIsMobileMenuOpen(false)}></div>
      <div className={`mobile-sidebar ${isMobileMenuOpen ? 'show' : ''}`}>
        <div className="mobile-sidebar-header">
          <span className="mobile-sidebar-title">Menu</span>
        </div>

        <div className="mobile-search-wrapper">
          <input className="mobile-search-input" type="text" placeholder="Search..." />
          <svg className="mobile-search-icon" fill="#666" viewBox="0 0 1024 1024" width="18" height="18">
            <path d="M1005.109936 914.29266l-250.119943-250.37582a417.591556 417.591556 0 0 0-632.592355-541.436111 417.719495 417.719495 0 0 0 295.410203 713.065729 414.393091 414.393091 0 0 0 246.345754-80.79322l250.119942 250.37582a64.225172 64.225172 0 0 0 90.900368-90.836398zM203.190857 632.827763a303.662242 303.662242 0 1 1 214.93683 89.109227A304.301935 304.301935 0 0 1 203.190857 632.827763z" />
          </svg>
        </div>

        <div className="mobile-lang-section">
          <span className="mobile-section-label">Language</span>
          <div className="mobile-lang-list">
            {languages.map((lang) => (
              <button
                key={lang.code}
                className={`mobile-lang-item ${currentLang === lang.code ? 'active' : ''}`}
                onClick={() => handleLanguageChange(lang.code)}
              >
                <span className="mobile-lang-flag">{lang.flag}</span>
                <span className="mobile-lang-name">{lang.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mobile-nav-container">
          {displayNavItems.map((link, index) => renderMobileMenuItem(link, 0, `root-${index}`))}
        </div>
      </div>

      <style>{`
        /* PC端样式 */
        .navbar.scrolled input { color: #333 !important; }
        .topInp { border: 1px solid rgb(223, 223, 223); }
        .scrolled .topInp { border: solid 1px #7c7c7c !important; }
        .scrolled .navbar-container svg { fill: #333; }
        .navbar-links { display: flex; align-items: center; gap: 30px; list-style: none; margin: 0; padding: 0; flex: 1; justify-content: center; }
        .navbar-link { display: flex; align-items: center; gap: 5px; text-decoration: none; color: #fff; font-weight: 500; font-size: 15px; padding: 26px 17px; position: relative; transition: color 0.3s ease; height: 44px; justify-content: center; align-items: center; overflow: hidden; }
        .navbar.scrolled .navbar-link { color: #333; }
        .link-bg { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background-repeat: no-repeat; background-size: auto 60%; background-position: center; z-index: 0; }
        .link-bg-gif { background-image: url('${processImageUrl("https://oss.yy-aicloud.com/uploads/69b78a2f5eb21494c08f7a144d3cc4cfa661ed1244039.gif")}'); }
        .link-bg-png { background-image: url('${processImageUrl("https://oss.yy-aicloud.com/uploads/69ae6c8f9af0ba19883fca95d0e5ec7ee6c94c6c32028.png")}'); }
        .link-text { position: relative; z-index: 1; display: flex; align-items: center; gap: 5px; }
        .dropdown-arrow { transition: transform 0.3s ease; margin-left: 2px; }
        .dropdown-arrow.rotate { transform: rotate(180deg); }
        .navbar-item-has-children { position: relative; }
        .dropdown-menu-vertical { position: absolute; top: 100%; left: 50%; transform: translateX(-50%) translateY(10px); background: white; border-radius: 8px; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15); min-width: 280px; max-width: 320px; list-style: none; opacity: 0; visibility: hidden; transition: all 0.3s ease; margin-top: 10px; z-index: 1000; overflow: hidden; }
        .dropdown-menu-vertical::before { content: ''; position: absolute; top: -6px; left: 50%; transform: translateX(-50%) rotate(45deg); width: 12px; height: 12px; background: white; }
        .dropdown-menu-vertical.show { opacity: 1; visibility: visible; transform: translateX(-50%) translateY(0); }
        .dropdown-menu-content { max-height: 900px; overflow-y: auto; padding: 8px 0; }
        .nested-menu-item { display: flex; flex-direction: column; }
        .nested-menu-row { display: flex; align-items: center; position: relative; }
        .menu-item-wrapper { display: flex; align-items: center; flex: 1; min-height: 40px; border-left: 3px solid transparent; transition: all 0.2s ease; cursor: pointer; }
        .menu-item-wrapper:hover { background: #f8f9fa; border-left-color: #4cb5de; }
        .nested-menu-link { display: flex; align-items: center; justify-content: space-between; padding: 10px 0; color: #333; text-decoration: none; font-size: 14px; transition: all 0.2s ease; flex: 1; background: transparent; }
        .nested-menu-link:hover { color: #4cb5de; }
        .nested-menu-link.active { color: #4cb5de; font-weight: 600; }
        .nested-menu-link.level-1 { font-weight: 600; font-size: 14px; }
        .nested-menu-link.level-2 { font-size: 13px; color: #444; font-weight: 500; }
        .nested-menu-link.level-3 { font-size: 12px; color: #666; }
        .menu-text { flex: 1; }
        .expand-btn { display: flex; align-items: center; justify-content: center; width: 28px; height: 28px; border: none; background: transparent; cursor: pointer; border-radius: 4px; transition: all 0.2s ease; margin-right: 8px; color: #666; }
        .expand-arrow { transition: transform 0.3s ease; }
        .expand-arrow.expanded { transform: rotate(90deg); }
        .expand-btn.expanded { color: #4cb5de; }
        .nested-children { background: #fafbfc; border-left: 2px solid #e9ecef; margin-left: 0; overflow: hidden; transition: all 0.3s ease; }
        .nested-children.expanded { max-height: 1000px; opacity: 1; }
        .nested-children.collapsed { max-height: 0; opacity: 0; }
        .badge { background: #ff4757; color: white; font-size: 11px; padding: 2px 6px; border-radius: 10px; margin-left: 5px; flex-shrink: 0; }
        .language-switcher { position: relative; margin-left: 30px; }
        .language-trigger { background: #fff0; border: none; display: flex; align-items: center; gap: 6px; padding: 8px 12px; border-radius: 8px; cursor: pointer; transition: all 0.3s ease; font-size: 14px; color: #fff; }
        .navbar.scrolled .language-trigger { color: #333; }
        .language-flag { font-size: 18px; line-height: 1; }
        .language-code { font-weight: 600; font-size: 13px; min-width: 24px; text-align: center; }
        .language-arrow { transition: transform 0.3s ease; opacity: 0.7; }
        .language-arrow.rotate { transform: rotate(180deg); }
        .language-dropdown { position: absolute; top: calc(100% + 10px); right: 0; background: white; border-radius: 12px; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15); min-width: 220px; opacity: 0; visibility: hidden; transform: translateY(-10px); transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); z-index: 1001; overflow: hidden; }
        .language-dropdown.show { opacity: 1; visibility: visible; transform: translateY(0); }
        .language-dropdown-header { padding: 12px 16px; background: #f8f9fa; border-bottom: 1px solid #e9ecef; font-size: 12px; font-weight: 600; color: #6c757d; text-transform: uppercase; letter-spacing: 0.5px; }
        .language-list { list-style: none; margin: 0; padding: 8px 0; max-height: 900px; overflow-y: auto; }
        .language-option { display: flex; align-items: center; gap: 12px; width: 100%; padding: 12px 16px; border: none; background: transparent; cursor: pointer; transition: all 0.2s ease; text-align: left; color: #333; }
        .language-option:hover { background: #f8f9fa; }
        .language-option.active { background: #e8f5e9; color: #2e7d32; }
        .language-option-flag { font-size: 20px; line-height: 1; }
        .language-option-info { display: flex; flex-direction: column; flex: 1; gap: 2px; }
        .language-option-name { font-weight: 500; font-size: 14px; }
        .language-option-local { font-size: 12px; color: #6c757d; }
        .check-icon { color: #4caf50; flex-shrink: 0; }
        .search-wrapper { position: relative; }

        /* 移动端基础元素 */
        .mobile-menu-btn, .mobile-sidebar, .mobile-menu-overlay { display: none; }

        /* 平板适配 */
        @media (max-width: 1024px) and (min-width: 769px) {
         .mobile-nav-link{background-position:0px 0.5rem !important;}
          .navbar{padding:0px !important;}
          .navbar-container { padding: 0 20px; }
          .navbar-links { gap: 15px; }
          .navbar-link { font-size: 13px; padding: 20px 12px; }
          .language-switcher { margin-left: 15px; }
          .search-wrapper input { width: 120px !important; font-size: 13px !important; padding: 5px 15px !important; }
          .navbar-logo { width: 130px !important; height: 37px !important; }
        }

        /* 手机适配 */
        @media (max-width: 768px) {
           .mobile-nav-link{background-position:0px 0.5rem !important;}
          .navbar{padding:0px !important;}
          .desktop-only { display: none !important; }
          .mobile-menu-btn, .mobile-sidebar, .mobile-menu-overlay { display: block; }
          .navbar-container { padding: 0 15px; height: 60px; justify-content: space-between; }
          .navbar-logo { width: 120px !important; height: 34px !important; }
          .mobile-menu-btn { display: flex; flex-direction: column; justify-content: center; align-items: center; width: 40px; height: 40px; background: transparent; border: none; cursor: pointer; padding: 8px; gap: 5px; z-index: 1002; }
          .hamburger-line { display: block; width: 22px; height: 2px; background: #fff; transition: all 0.3s ease; border-radius: 1px; }
          .navbar.scrolled .hamburger-line { background: #333; }
          .mobile-menu-btn.active .hamburger-line:nth-child(1) { transform: rotate(45deg) translate(5px, 5px); }
          .mobile-menu-btn.active .hamburger-line:nth-child(2) { opacity: 0; }
          .mobile-menu-btn.active .hamburger-line:nth-child(3) { transform: rotate(-45deg) translate(5px, -5px); }
          .mobile-menu-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.5); opacity: 0; visibility: hidden; transition: all 0.3s ease; z-index: 998; }
          .mobile-menu-overlay.show { opacity: 1; visibility: visible; }
          .mobile-sidebar { position: fixed; top: 0; right: -100%; width: 80%; max-width: 320px; height: 100vh; background: #fff; box-shadow: -5px 0 30px rgba(0, 0, 0, 0.1); transition: right 0.3s ease; z-index: 999; overflow-y: auto; display: flex; flex-direction: column; }
          .mobile-sidebar.show { right: 0; }
          .mobile-sidebar-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid #e9ecef; background: #f8f9fa; }
          .mobile-sidebar-title { font-size: 18px; font-weight: 600; color: #333; margin-top: 15px; }
          .mobile-close-btn { background: transparent; border: none; cursor: pointer; color: #666; padding: 5px; display: flex; align-items: center; justify-content: center; }
          .mobile-search-wrapper { position: relative; padding: 15px 20px; border-bottom: 1px solid #e9ecef; }
          .mobile-search-input { width: 100%; padding: 12px 40px 12px 15px; border: 1px solid #ddd; border-radius: 25px; font-size: 14px; background: #f8f9fa; color: #333; }
          .mobile-search-input::placeholder { color: #999; }
          .mobile-search-icon { position: absolute; right: 30px; top: 50%; transform: translateY(-50%); cursor: pointer; }
          .mobile-lang-section { padding: 15px 20px; border-bottom: 1px solid #e9ecef; }
          .mobile-section-label { display: block; font-size: 12px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }
          .mobile-lang-list { display: flex; flex-wrap: wrap; gap: 8px; }
          .mobile-lang-item { display: flex; align-items: center; gap: 6px; padding: 8px 12px; border: 1px solid #e9ecef; border-radius: 20px; background: #fff; cursor: pointer; transition: all 0.2s ease; font-size: 13px; }
          .mobile-lang-item.active { background: #4cb5de; color: #fff; border-color: #4cb5de; }
          .mobile-lang-flag { font-size: 16px; }
          .mobile-nav-container { flex: 1; overflow-y: auto; }
          .mobile-menu-item { border-bottom: 1px solid #f0f0f0; }
          .mobile-nav-link-wrapper { display: flex; align-items: center; justify-content: space-between; min-height: 50px; }
          .mobile-nav-link { display: flex; align-items: center; justify-content: space-between; padding: 14px 0; color: #333; text-decoration: none; font-size: 15px; font-weight: 500; flex: 1; transition: all 0.2s ease; }
          .mobile-nav-link:hover, .mobile-nav-link.active { color: #4cb5de; }
          .mobile-nav-link.external { color: #666; }
          .mobile-nav-link.external svg { margin-left: 8px; }
          .mobile-expand-btn { display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; background: transparent; border: none; cursor: pointer; color: #666; transition: all 0.3s ease; flex-shrink: 0; }
          .mobile-expand-btn svg { transition: transform 0.3s ease; }
          .mobile-expand-btn.expanded svg { transform: rotate(90deg); }
          .mobile-submenu { background: #f8f9fa; overflow: hidden; max-height: 0; transition: max-height 0.3s ease; }
          .mobile-submenu.expanded { max-height: 2000px; }
          .mobile-menu-item .mobile-menu-item { border-bottom: 1px solid #e9ecef; }
          .mobile-menu-item .mobile-menu-item:last-child { border-bottom: none; }
          .mobile-nav-container > .mobile-menu-item > .mobile-nav-link-wrapper .mobile-nav-link { font-size: 16px; font-weight: 600; }
          .mobile-nav-container > .mobile-menu-item > .mobile-submenu > .mobile-menu-item > .mobile-nav-link-wrapper .mobile-nav-link { font-size: 15px; font-weight: 500; color: #444; }
          .mobile-nav-container > .mobile-menu-item > .mobile-submenu > .mobile-menu-item > .mobile-submenu > .mobile-menu-item > .mobile-nav-link-wrapper .mobile-nav-link { font-size: 14px; font-weight: 400; color: #555; }
          .mobile-nav-container > .mobile-menu-item > .mobile-submenu > .mobile-menu-item > .mobile-submenu > .mobile-menu-item > .mobile-submenu > .mobile-menu-item > .mobile-nav-link-wrapper .mobile-nav-link { font-size: 13px; font-weight: 400; color: #666; }
        }

        @media (max-width: 375px) {
         .mobile-nav-link{background-position:0px 0.5rem !important;}
          .navbar{padding:0px !important;}
          .mobile-sidebar { width: 85%; }
          .mobile-nav-link { font-size: 14px; }
          .mobile-sidebar-header { padding: 15px; }
          .mobile-search-wrapper { padding: 12px 15px; }
          .mobile-lang-section { padding: 12px 15px; }
        }
      `}</style>
      
    </nav>
  );
}

// 导出组件，使用 Suspense 包裹以避免 SSR 错误
export default function Navbar({ isVisible }: NavbarProps) {
  return (
    <Suspense fallback={<nav className="navbar" style={{ height: '80px' }} />}>
      <NavbarInternal isVisible={isVisible} />
    </Suspense>
  );
}